const config = {
    personalInfo: {
        name: "Devang Saraogi",
        title: "CS Grad @ NC State University",
        tagline:
            "An aspiring researcher with a deep curiosity about the transformative potential of machine learning.",
        location: "Raleigh, NC",
        emails: ["dsaraog@ncsu.edu", "devangsaraogi@outlook.com"],
        mobile: "+1 (919) 521 3303",
        languages: ["English", "Hindi"],
        education: [
            {
                degree: "Master of Science in Computer Science",
                school: "North Carolina State University",
                date: "AUG '24 — MAY '26",
                location: "Raleigh, NC",
                gpa: "3.82/4.00",
                description:
                    "Pursuing a Master's degree, exploring advanced deep learning concepts and their practical applications through coursework and hands-on projects.",
                courses: [
                    "Deep Learning",
                    "Graduate Operating Systems",
                    "Algorithms",
                    "Software Engineering & Design",
                    "OOPs",
                ],
            },
            {
                degree: "Bachelor of Technology in Computer Science",
                school: "PES University",
                date: "AUG '20 — MAY '24",
                location: "Bangalore, India",
                gpa: "7.54/10.00",
                description:
                    "Completed undergraduate studies in Computer Science, building a strong foundation in core fundamentals while specializing in Machine Intelligence and Data Science through elective coursework.",
                courses: [
                    "Machine Intelligence",
                    "Cloud Computing",
                    "Databases",
                    "Big Data",
                    "Data Analytics",
                ],
            },
        ],
    },

    technicalSkills: {
        domain: [
            "Artificial Intelligence",
            "Generative AI",
            "Machine Learning",
            "Deep Learning",
            "NLP",
            "Software Development",
            "Data Analytics",
            "Prompt Engineering",
        ],
        languages: [
            "Python",
            "JavaScript/TypeScript",
            "SQL",
            "C/C++",
            "HTML/CSS",
            "Shell",
        ],
        frameworkTools: [
            "PyTorch",
            "LangChain",
            "LangGraph",
            "scikit-learn",
            "React",
            "Node.js",
            "FastAPI",
            "Flask",
            "Selenium",
            "Git",
            "Docker",
            "PostgreSQL/pgvector",
        ],
        libraries: ["OpenCV", "NumPy", "Pandas", "Scikit-Image"],
    },

    about: [
        "I am a graduate Computer Science student focused on applied machine learning, with experience building the systems and data pipelines required to deploy ML in real-world settings.",
        "My work spans model training, evaluation, and robustness analysis, including projects on distribution shift evaluation, transaction classification using BERT, and vision–based verification systems. To support these ML workflows, I have built scalable data pipelines, distributed systems, and operating system components, enabling reliable experimentation and production deployment.",
        "I am particularly interested in machine learning engineering roles where model performance, data quality, and system design intersect to solve practical problems at scale.",
    ],

    experiences: [
        {
            type: "industry",
            date: "MAY — OCT 2025",
            role: "Data Analyst Intern",
            company: "NetApp",
            companyUrl: "https://www.netapp.com/",
            location: "Durham, North Carolina",
            description:
                "At NetApp, I worked on applied data analysis and machine learning to understand customer usage and health patterns at scale. I applied unsupervised learning techniques to customer KPI data, built scalable data preprocessing pipelines for large datasets, and analyzed account segments to surface measurable differences in health indicators. I also delivered a proof-of-concept analytics framework demonstrating how clustering-based insights could inform account management and customer health strategy.",
            skills: ["ML", "Python", "Pandas", "Snowflake", "SQL"],
        },
        {
            type: "industry",
            date: "JAN — JUN 2024",
            role: "ML Engineering Intern",
            company: "IDfy Labs",
            companyUrl: "https://www.idfy.com/",
            location: "Mumbai, India",
            description:
                "At IDfy Labs, I worked on applied machine learning within a production-grade KYC system. My work included building ML-driven verification components such as speech-adaptive user workflows and vision based document and address verification. When labeled data was insufficient, I created transaction categorization datasets by scraping merchant information and synthesizing realistic transaction narratives, and trained a BERT-based model to support downstream classification.",
            skills: ["ML", "Python", "JavaScript", "React", "SQL"],
        },
        {
            type: "research",
            date: "JUN — JULY 2023",
            role: "Summer Research Intern",
            company: "CAVE Labs, PESU",
            companyUrl: "https://www.iot.pes.edu/cave",
            location: "Bangalore, India",
            description:
                "Developed an automated personality generation system and integrated it into a Unity environment to create diverse patient personalities. Tuned LLM parameters (GPT, LLaMA, Open Assistant) and applied prompt engineering to generate personality traits for input into Inworld AI. Authored a research paper (under review) on leveraging LLMs for personality generation.",
            skills: ["Python", "Selenium", "Unity"],
        },
    ],

    projects: [
        {
            title: "AI Agent Marketplace - Kova",
            external: "https://github.com/RandomOscillations/kova",
            description:
                "Hackathon-winning platform where AI agents post bounty-based tasks, humans fulfill them, and payment releases through Solana escrow. Features a four-tier trust scoring system for automated supervisor ranking and deterministic payment flows. Built in 24 hours at Hack_NCState 2026.",
            tech: ["TypeScript", "Node.js", "Blockchain", "WebSocket"],
            image: "/images/projects/kova.png",
            year: "2026",
        },
        {
            title: "Model Calibration Under Distribution Shift",
            external:
                "https://github.com/devangsaraogi/calibration-under-corruption",
            description:
                "Designed a reproducible evaluation pipeline to study confidence calibration and robustness under distribution shift using ResNet-18. Ran large-scale experiments across corruption benchmarks and analyzed calibration/accuracy trade-offs (ECE, NLL) on an HPC GPU cluster.",
            tech: ["Python", "PyTorch", "Bash", "HPC", "ResNet-18"],
            image: "/images/projects/calibrate.jpeg",
            year: "2025",
        },
        {
            title: "Operating Systems Course Projects",
            external:
                "https://github.com/devangsaraogi/xinu-operating-system-projects",
            description:
                "Extended the XINU kernel by implementing Linux-style and probabilistic CPU schedulers, a demand-paged virtual memory subsystem with swap-backed mappings and Second-Chance replacement, and a UNIX-style disk defragmenter that reorganizes inode and block layouts to improve locality and I/O efficiency.",
            tech: ["C", "XINU"],
            image: "/images/projects/os.png",
            year: "2025",
        },
        {
            title: "CacheForge - Cache Replacement Policy Generation",
            external: "https://github.com/devangsaraogi/cache-policy-gen",
            description:
                "An LLM-powered system that automatically generates C++ cache replacement policies, compiles and tests them in the ChampSim CRC-2 simulator, and iteratively improves them using RAG-seeded prompts from top-performing past policies. Achieved ~4% hit rate improvement over LRU/SRRIP baselines.",
            tech: ["Python", "C++", "Llama", "SQLite", "RAG"],
            image: "/images/projects/cache_gen.jpg",
            year: "2025",
        },
        {
            title: "RAG-Based Financial Document Analysis",
            external: "https://github.com/devangsaraogi/doc-qa-pipeline",
            description:
                "Full-stack AI financial advisor assistant where users upload financial documents and query them in natural language across Q&A, Summary, Risk Assessment, and Client Email modes. Built with a LangGraph RAG pipeline, pgvector with HNSW indexing for retrieval, relevance filtering with fallback, source attribution, and on-premises Llama 3.2 inference for cost and privacy.",
            tech: ["LangGraph", "pgvector", "Llama", "FastAPI", "React", "Docker"],
            image: "/images/projects/fin-rag.jpeg",
            year: "2025",
        },
        {
            title: "Pose Estimation & Object Detection",
            external: "",
            description:
                "A virtual gym trainer that delivers real-time feedback on exercise posture and dumbbell usage. Features YOLO-v8 model for dumbbell orientation estimation and Random Forest Classifier for posture analysis.",
            tech: ["Python", "MediaPipe", "YOLO Models", "Random Forest"],
            image: "/images/projects/pose-estimation.png",
            year: "2024",
        },
        {
            title: "Leukocyte Classification",
            external: "",
            paperLink: "https://ieeexplore.ieee.org/document/10126331",
            description:
                "Deep learning system for leukocyte classification in pathology labs. Leverages transfer learning with VGG16, ResNet, and InceptionV3 for accurate blood cell classification.",
            tech: ["Python", "OpenCV", "PyTorch", "Transfer Learning"],
            image: "/images/projects/wbc-paper.png",
            year: "2023",
        },
        {
            title: "Distributed Event Streaming Platform",
            external: "",
            description:
                "Built a Kafka-inspired event streaming system using socket programming, supporting producers/consumers/brokers with partitioned topics. Added ZooKeeper-style coordination for broker monitoring and leader election, enabling fault tolerance via replication.",
            tech: ["Python", "Socket Programming"],
            image: "/images/projects/kafka.png",
            year: "2024",
        },
        {
            title: "Optical Character Recognition",
            external: "",
            description:
                "Automated sudoku solver using OpenCV and CNN trained on MNIST dataset. Complete pipeline from image processing to puzzle solving with board detection and digit recognition.",
            tech: ["Python", "OpenCV", "Scikit-Image", "CNN", "MNIST"],
            image: "/images/projects/sudoku-ocr.png",
            year: "2023",
        },
        {
            title: "MasterMind Game",
            external: "https://github.com/devangsaraogi/mastermind",
            description:
                "Implemented the classic MasterMind game with Tkinter interface. Features include a guessing mechanism, feedback system, and a scoring algorithm.",
            tech: ["Python", "Tkinter"],
            image: "/images/projects/mastermind.png",
            year: "2023",
        },
    ],

    socialLinks: {
        github: "https://github.com/devangsaraogi",
        linkedin: "https://linkedin.com/in/devangsaraogi",
        x: "https://x.com/devangsaraogi",
    },

    resumeLink: "/resume.pdf",

    achievements: [
        {
            date: "FEB 2026",
            title: "1st Place — Hack_NCState 2026",
            description:
                "Won first place at Hack_NCState (MLH) for building Kova, a decentralized AI agent marketplace with Solana escrow and automated trust scoring. Built in 24 hours.",
        },
    ],

    volunteering: [
        {
            date: "JAN — MAY 2024",
            role: "Teaching Assistant",
            course: "Topics in Deep Learning",
            institution: "PES University",
            location: "Bangalore, India",
            description:
                "Assisted in conducting labs and tutorials, helped students with assignments and projects, and provided guidance in deep learning concepts.",
        },
    ],
};

export default config; 